import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { WashDtoAll } from "@/common/mapping/mappers/wash";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import { WashDto, WashReferenceDto } from "@/core/api/generated/v1";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import { getVehicleInlineTitleShort } from "../Vehicle/VehicleInline";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import { WashViewModal } from "./View/WashViewModal";

const defaultDisplayProps = { vehicle: true };
type DisplayProps = typeof defaultDisplayProps;

export const getWashInlineTitle = (data: Nil<WashDtoAll>, displayProps?: DisplayProps) =>
  data
    ? StringHelper.join(
        [
          EntityHelper.getNumberTitle(data),
          enumService.getEnumValueName(ApiEnumName.WashStage, data.stageHistory?.stage),
          displayProps?.vehicle && data.vehicle
            ? getVehicleInlineTitleShort(data.vehicle)
            : undefined,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;

export interface WashInlineProps extends BaseEntityInlineInheritableProps {
  entity: WashDto | WashReferenceDto | null | undefined;
  entityId?: string | null;
  displayProps?: Partial<DisplayProps>;
}

export default function WashInline({
  entity,
  entityId,
  displayProps,
  fetch = true,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: WashInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.washesApi.apiV1WashesWashIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      washId: entityId!,
    },
    {
      deps: [entityId],
      skip: !fetch || !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const entityIdComputed = entity?.id || entityId;

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={fetch ? request : undefined}
        icon={<AppIcon of='wash' inText />}
        content={getWashInlineTitle(entity, displayPropsComputed) || "Wash"}
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Wash"}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <WashViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                washId: entityIdComputed,
                wash: EntityHelper.nullIfNotFull(entity),
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
